import React, { useEffect, useState, useCallback } from "react";
import { Sun, Moon } from "@phosphor-icons/react";

// v12.5 — Sun/Moon theme toggle. Reads + writes `localStorage.crow_theme`
// AND applies the resulting value to `<html data-theme>` so the invert
// filter defined in index.css takes over globally. Fires a `storage`
// event manually so any OTHER tab/route that also mounts a toggle picks
// up the change instantly (localStorage `storage` events only cross
// tabs, not same-tab listeners).
export default function ThemeToggle({ size = 16, className = "" }) {
  const readInitial = () => (localStorage.getItem("crow_theme") === "light" ? "light" : "dark");
  const [theme, setTheme] = useState(readInitial);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Cross-component sync — when another mounted ThemeToggle flips the
  // value, listen for our custom event and update local state.
  useEffect(() => {
    const onChange = () => setTheme(readInitial());
    window.addEventListener("crow-theme-change", onChange);
    return () => window.removeEventListener("crow-theme-change", onChange);
  }, []);

  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      localStorage.setItem("crow_theme", next);
      document.documentElement.setAttribute("data-theme", next);
      window.dispatchEvent(new Event("crow-theme-change"));
      return next;
    });
  }, []);

  const isLight = theme === "light";
  return (
    <button
      onClick={toggle}
      className={`btn-3d w-10 h-10 flex items-center justify-center flex-shrink-0 ${className}`}
      title={isLight ? "Switch to dark mode" : "Switch to light mode"}
      aria-label="Toggle theme"
      data-testid="theme-toggle"
    >
      {isLight ? <Moon size={size} weight="fill" /> : <Sun size={size} weight="fill" />}
    </button>
  );
}
